import {ReactElement} from 'react'
import {Trans} from 'react-i18next'
import {WithdrawalVendorEnumIndices, withdrawalPaymentVendors} from '@bdswiss/common-enums'

import messages from '../../../assets/messages'
import {Account} from '../../../graphql/types'
import {
  getTauroFactorisationFromAccount,
  TauroAccountFactorisation,
  TauroRefundMethod,
} from '../../../common/utils/accounts'
import {WithdrawalMethodValues} from './WithdrawalSummary/constants'
import {RefundWithdrawSchemaItem} from './WithdrawalSummary/types'

/** The payout leg the server expects on every item of a factorised withdrawal (PT-6618). */
export enum TauroPayoutLeg {
  refund = 'REFUND',
  profit = 'PROFIT',
}

/** The profit leg goes to Crypto or Bank Transfer only, the same rule the server applies. */
export const TAURO_PROFIT_METHODS: string[] = [
  WithdrawalMethodValues.bankTransfer,
  WithdrawalMethodValues.cryptocurrency,
  WithdrawalMethodValues.enovipayCryptoPayout,
  WithdrawalMethodValues.dfnsCryptoPayout,
]

/** Refund vendors that the withdrawal form can send without extra client input. */
const TAURO_REFUND_VENDORS: string[] = [
  WithdrawalVendorEnumIndices.enovipayRefund,
  WithdrawalVendorEnumIndices.enovipayMobileWalletRefund,
  WithdrawalVendorEnumIndices.enovipayKnetRefund,
]

/**
 * The factorisation of a funded Tauro account, or null for a regular or unfunded account.
 * A funded account withdraws only through the one-time payout flow (PT-6615).
 */
export const getTauroWithdrawal = (
  account: Account | null | undefined,
): TauroAccountFactorisation | null => {
  if (!account) return null
  const factorisation = getTauroFactorisationFromAccount(account)
  return factorisation?.clientPayout != null ? factorisation : null
}

/**
 * The refund leg as a refund schema item, or null when the form cannot send the refund to the
 * method of the first deposit. The card refund needs the last card digits in its payment fields.
 */
export const buildTauroRefundItem = (
  factorisation: TauroAccountFactorisation,
): RefundWithdrawSchemaItem | null => {
  const {refundMethod, refundPayout} = factorisation
  const vendor = refundMethod?.paymentVendor
  if (!vendor || !TAURO_REFUND_VENDORS.includes(vendor) || !refundPayout) return null
  if (vendor === WithdrawalVendorEnumIndices.enovipayRefund && !refundMethod?.cardNumber) return null
  return {
    id: 'tauro-refund',
    amount: refundPayout,
    details: refundMethod?.cardNumber || '',
    vendor,
  }
}

/** Masked name of the original payment method, e.g. "Card •••• 4821". */
export const TauroRefundMethodName = ({
  refundMethod,
}: {
  refundMethod: TauroRefundMethod | null | undefined
}): ReactElement | null => {
  if (!refundMethod?.paymentVendor) return null
  if (refundMethod.cardNumber) {
    return (
      <Trans {...messages.tauroRefundMethodCard} values={{cardNumber: refundMethod.cardNumber}} />
    )
  }
  return <>{withdrawalPaymentVendors[refundMethod.paymentVendor]?.label ?? refundMethod.paymentVendor}</>
}
